import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ShareWhatsApp from "@/components/ShareWhatsApp";

const STATUSES = ["open", "in_progress", "resolved"];
const STATUS_STYLE = {
    open: "bg-nest-warn text-white",
    in_progress: "bg-nest-info text-white",
    resolved: "bg-nest-sage text-white",
};

export default function Complaints() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ title: "", description: "", category: "general" });

    const load = async () => {
        try { const r = await api.get("/complaints"); setItems(r.data); } catch {}
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const r = await api.post("/complaints", form);
            setItems((arr) => [r.data, ...arr]);
            setForm({ title: "", description: "", category: "general" });
            toast.success("Complaint raised");
        } catch { toast.error("Failed"); }
    };

    const setStatus = async (c, status) => {
        try {
            const r = await api.patch(`/complaints/${c.complaint_id}`, { status });
            setItems((arr) => arr.map((x) => x.complaint_id === c.complaint_id ? r.data : x));
            toast.success("Updated");
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="animate-fade-up" data-testid="complaints-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 sm:gap-4">
                <div>
                    <p className="nest-stat-label">Helpdesk</p>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1 sm:mt-2">{isAdmin ? "Helpdesk queue" : "Complaints & requests"}</h1>
                    <p className="text-nest-ink2 text-sm sm:text-base mt-1 sm:mt-2">{isAdmin ? "Triage and resolve resident issues." : "Tell the admin what needs fixing."}</p>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 mt-6 sm:mt-10">
                {!isAdmin && user?.role === "resident" && (
                    <form onSubmit={submit} className="nest-card p-6 self-start" data-testid="complaint-form">
                        <p className="nest-stat-label">Raise an issue</p>
                        <h3 className="font-display text-xl mt-1 tracking-tight">What's broken?</h3>
                        <div className="mt-5 grid gap-4">
                            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="complaint-title-input"/>
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="complaint-category-input">
                                <option value="general">General</option>
                                <option value="plumbing">Plumbing</option>
                                <option value="electrical">Electrical</option>
                                <option value="security">Security</option>
                                <option value="cleaning">Cleaning</option>
                                <option value="other">Other</option>
                            </select>
                            <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the issue" className="bg-transparent border border-nest-line rounded-md focus:border-nest-clay outline-none p-3" data-testid="complaint-desc-input"/>
                            <button type="submit" className="nest-btn-primary" data-testid="complaint-submit-btn">Submit</button>
                        </div>
                    </form>
                )}
                <div className={`${(!isAdmin && user?.role === "resident") ? "lg:col-span-2" : "lg:col-span-3"} space-y-4`}>
                    {items.length === 0 && <div className="nest-card p-10 text-center text-nest-ink2" data-testid="no-complaints">Nothing here. Quiet streets, happy neighbours.</div>}
                    {items.map((c) => (
                        <article key={c.complaint_id} className="nest-card p-6" data-testid={`complaint-${c.complaint_id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-nest-clay">{c.category}</span>
                                    <h3 className="font-display text-2xl tracking-tight mt-1">{c.title}</h3>
                                </div>
                                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${STATUS_STYLE[c.status]}`}>{c.status.replace("_", " ")}</span>
                            </div>
                            <p className="text-nest-ink2 mt-3 whitespace-pre-wrap leading-relaxed">{c.description}</p>
                            <div className="nest-divider my-4"></div>
                            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-nest-ink3">
                                <span>{c.user_name}{c.flat_no ? ` · ${c.flat_no}` : ""} · {new Date(c.created_at).toLocaleString()}</span>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <ShareWhatsApp
                                        testid={`complaint-share-${c.complaint_id}`}
                                        label="Share"
                                        text={`🛠️ *${c.title}* [${c.status.replace("_", " ")}] (${c.category})\n\n${c.description}\n\nFlat: ${c.flat_no || "—"} · ${c.user_name}\n— NeighborNest`}
                                    />
                                    {isAdmin && STATUSES.map((s) => (
                                        <button key={s} onClick={() => setStatus(c, s)} disabled={c.status === s} className={`text-[10px] px-2 py-1 rounded uppercase tracking-widest border ${c.status === s ? "bg-nest-ink text-white border-nest-ink" : "border-nest-line hover:border-nest-ink"}`} data-testid={`complaint-${c.complaint_id}-${s}`}>{s.replace("_", " ")}</button>
                                    ))}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
}
